import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ParamType } from "../../../../types/grid.ts";
import type { InputParam } from "../../../../types/grid.ts";
import type { FreeTextIdentityConfig, GroupFieldDef } from "../freeTextIdentityFilter.tsx";
import styles from "./shared.module.scss";
import { POPOVER_VIEWPORT_MARGIN, placeVertically, type VerticalPlacement } from "./popoverPlacement.ts";

type PopoverCoords = VerticalPlacement & { left: number };

export { styles as sharedStyles };

export interface VariantProps<T> {
  config: FreeTextIdentityConfig<T>;
  columnName: string;
  isParameter: boolean;
  value: InputParam[];
  onChange: (value: InputParam[]) => void;
  onSearch: () => void;
}

/** Turn an identity string into an onChange payload with the right ParamType. */
export function useIdentityEmit(
  columnName: string,
  isParameter: boolean,
  onChange: (value: InputParam[]) => void,
) {
  return useCallback(
    (text: string) => {
      if (!text.trim()) {
        onChange([]);
        return;
      }
      const paramType = isParameter ? ParamType.Specified : ParamType.In;
      onChange([{ sColumn: columnName, oValue: text, Type: paramType }]);
    },
    [columnName, isParameter, onChange],
  );
}

/** Read initial identity text out of the current value prop (first param's oValue). */
export function getInitialIdText(value: InputParam[]): string {
  if (value.length > 0 && value[0]!.oValue != null) return String(value[0]!.oValue);
  return "";
}

/** Build a distinct id → item lookup for quick chip labelling. */
export function useItemById<T>(config: FreeTextIdentityConfig<T>): Map<number, T> {
  return useMemo(() => {
    const m = new Map<number, T>();
    for (const it of config.items) m.set(config.getIdFromItem(it), it);
    return m;
  }, [config]);
}

/** Close on outside click. Returns a ref to attach to the root container. */
export function useOutsideClose(onClose: () => void): React.RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);
  return ref;
}

/**
 * Portaled, viewport-fixed popover. Escapes ancestor `overflow:hidden`/`auto`
 * clipping — needed when the filter is rendered inside the chips-mode
 * `+ Filter` Modal, whose `.dialog` / `.body` clip absolutely-positioned
 * descendants. Positions itself from the anchor element's bounding rect and
 * re-measures on resize and when its content changes size. Opens below the
 * anchor, flips above when it would overflow the viewport bottom (anchors in
 * bottom-pinned panels, e.g. the map control panel), and caps itself with
 * internal scrolling when it fits on neither side — see `placeVertically`.
 * Outside clicks that hit neither the anchor nor the popover content trigger
 * `onClose`.
 */
export function FloatingPopover({
  open,
  anchorRef,
  onClose,
  children,
  align = "start",
  width,
  className,
  offsetY = 4,
}: {
  open: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  children: React.ReactNode;
  align?: "start" | "end";
  width?: number | string;
  className?: string;
  offsetY?: number;
}) {
  const popRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<PopoverCoords | null>(null);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    // Expected popover width for viewport-edge clamping. Fall back to 320 —
    // close to every call site's real min-width — when no explicit width is
    // provided.
    const w = typeof width === "number" ? width : 320;
    const preferredLeft = align === "end" ? r.right - w : r.left;
    const maxLeft = window.innerWidth - w - POPOVER_VIEWPORT_MARGIN;
    const clampedLeft = Math.max(POPOVER_VIEWPORT_MARGIN, Math.min(preferredLeft, maxLeft));
    // Natural height of the content (unaffected by any max-height we set on
    // the outer box) plus the outer box's borders. 0 before first mount — the
    // first pass then opens below, and the mount-time pass re-places it.
    const pop = popRef.current;
    const content = contentRef.current;
    const height = content ? content.offsetHeight + (pop ? pop.offsetHeight - pop.clientHeight : 0) : 0;
    const vertical = placeVertically(r, height, window.innerHeight, offsetY);
    setCoords((prev) => {
      const next: PopoverCoords = { left: clampedLeft, ...vertical };
      // Skip state update if the numbers are already identical — avoids a
      // second render cycle when resize / content observers fire unchanged.
      if (prev && prev.top === next.top && prev.left === next.left
        && prev.maxHeight === next.maxHeight && prev.flipped === next.flipped) return prev;
      return next;
    });
  }, [anchorRef, align, offsetY, width]);

  useLayoutEffect(() => {
    if (!open) { setCoords(null); return; }
    place();
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  /* Once the popover is in the DOM, measure it and re-place (flip above /
     clamp) — and keep doing so while its content changes size (filtering a
     list, switching tabs). Runs before paint, so an anchor near the bottom of
     the viewport never flashes the popover below the fold first. */
  const mounted = open && coords !== null;
  useLayoutEffect(() => {
    if (!mounted) return;
    place();
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => place());
    ro.observe(content);
    return () => ro.disconnect();
  }, [mounted, place]);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t)) return;
      if (popRef.current?.contains(t)) return;
      onClose();
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, anchorRef, onClose]);

  if (!open || !coords) return null;

  const style: React.CSSProperties = {
    position: "fixed",
    top: coords.top,
    left: coords.left,
    zIndex: 1000,
    ...(width != null ? { width } : {}),
    // Too tall for either side: cap at the room available and scroll inside.
    ...(coords.maxHeight != null ? { maxHeight: coords.maxHeight, overflowY: "auto" } : {}),
    // `.popover` carries a small top margin meant for the below-anchor case;
    // above the anchor it would eat into the gap instead.
    ...(coords.flipped ? { marginTop: 0 } : {}),
  };

  // Render inline (no portal). The popover is already a descendant of the
  // anchor's subtree, so when the anchor lives inside a native <dialog open>
  // the popover inherits the top-layer from the dialog; `position: fixed`
  // keeps it viewport-aligned regardless of the dialog's overflow. Portaling
  // to document.body would drop it out of the top layer and it'd hide behind
  // the dialog, and portaling into the dialog as a sibling of its header /
  // body / footer can collapse the dialog's flex layout in some states.
  return (
    <div ref={popRef} className={className} style={style}>
      <div ref={contentRef}>{children}</div>
    </div>
  );
}

/** Nouns used in placeholders ("Search kiosks…"). Falls back to the column name. */
export function getNoun<T>(config: FreeTextIdentityConfig<T>): string {
  if (config.idField === "KioskID") return "kiosk";
  if (config.idField === "LocationID") return "location";
  if (config.idField === "ProductID") return "product";
  return "item";
}

/** Group value map: each field → map of distinct value → ids. */
export type GroupData = Map<string, Map<string, number[]>>;

export function useGroupData<T>(config: FreeTextIdentityConfig<T>): GroupData | null {
  return useMemo(() => {
    if (!config.groupFields?.length) return null;
    const map: GroupData = new Map();
    for (const gf of config.groupFields) {
      const values = new Map<string, number[]>();
      for (const item of config.items) {
        const raw = gf.getValue
          ? gf.getValue(item)
          : (item as Record<string, unknown>)[gf.field];
        const list: unknown[] = Array.isArray(raw) ? raw : [raw];
        const id = config.getIdFromItem(item);
        const seenForItem = new Set<string>();
        for (const entry of list) {
          const val = entry == null ? "" : String(entry).trim();
          if (!val || seenForItem.has(val)) continue;
          seenForItem.add(val);
          const bucket = values.get(val);
          if (bucket) bucket.push(id);
          else values.set(val, [id]);
        }
      }
      if (values.size > 0) map.set(gf.field, values);
    }
    return map;
  }, [config]);
}

/* Icon shims — single source of truth lives in `components/ui/statusIcon.tsx`
   per AGENTS.md § Icons. These wrappers preserve the previous size defaults
   and named imports used across the identity-variant subsystem so callers
   don't have to change. */
import { StatusIcon } from "../../../ui/statusIcon.tsx";

export function XIcon({ size = 10 }: { size?: number }) {
  return <StatusIcon kind="close" size={size} />;
}

export function ChevronIcon({ size = 10 }: { size?: number }) {
  return <StatusIcon kind="chevronDown" size={size} style={{ opacity: 0.6 }} />;
}

export function SearchIcon({ size = 13 }: { size?: number }) {
  return <StatusIcon kind="search" size={size} />;
}

export function GridIcon({ size = 12 }: { size?: number }) {
  return <StatusIcon kind="grid" size={size} />;
}

export function HashIcon({ size = 12 }: { size?: number }) {
  return <StatusIcon kind="hash" size={size} />;
}

/** Removable chip showing "#id name". */
export function IdChip<T>({
  id,
  item,
  config,
  onRemove,
  dim,
}: {
  id: number;
  item: T | undefined;
  config: FreeTextIdentityConfig<T>;
  onRemove?: () => void;
  dim?: boolean;
}) {
  const name = item ? String((item as Record<string, unknown>)[config.nameField] ?? "") : "";
  return (
    <span className={dim ? styles.chipDim : styles.chip} title={name}>
      <span className={styles.chipId}>#{id}</span>
      {name && <span className={styles.chipLabel}>{name}</span>}
      {onRemove && (
        <button
          type="button"
          className={styles.chipClose}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Remove"
        >
          <XIcon />
        </button>
      )}
    </span>
  );
}

/** Render one suggestion row. */
export function SuggestRow<T>({
  item,
  config,
  selected,
  highlighted,
  onClick,
  showCheck,
}: {
  item: T;
  config: FreeTextIdentityConfig<T>;
  selected?: boolean;
  highlighted?: boolean;
  onClick: () => void;
  showCheck?: boolean;
}) {
  const id = config.getIdFromItem(item);
  const name = String((item as Record<string, unknown>)[config.nameField] ?? "");
  const meta = metaFor(item, config);
  return (
    <div
      className={`${styles.suggestItem}${highlighted ? ` ${styles.highlighted}` : ""}`}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {showCheck && (
        <input type="checkbox" checked={!!selected} readOnly style={{ margin: 0, pointerEvents: "none" }} />
      )}
      <span className={styles.suggestId}>#{id}</span>
      <span className={styles.suggestName}>{name}</span>
      {meta && <span className={styles.suggestMeta}>{meta}</span>}
    </div>
  );
}

function metaFor<T>(item: T, config: FreeTextIdentityConfig<T>): string {
  const r = item as Record<string, unknown>;
  const parts: string[] = [];
  if (r["City"]) parts.push(String(r["City"]));
  if (r["District"]) parts.push(String(r["District"]));
  if (!parts.length && r["Address"]) parts.push(String(r["Address"]));
  if (config.idField === "ProductID" && r["Other"]) {
    const o = r["Other"];
    parts.push(Array.isArray(o) ? o.filter((x) => typeof x === "string" && x).join(", ") : String(o));
  }
  const extra = config.extraMeta?.(item);
  if (extra) parts.push(extra);
  return parts.join(" · ");
}

export function groupValuesSorted(
  groupData: GroupData | null,
  field: string,
): Array<{ value: string; count: number }> {
  if (!groupData) return [];
  const m = groupData.get(field);
  if (!m) return [];
  return [...m.entries()]
    .map(([k, ids]) => ({ value: k, count: ids.length }))
    .sort((a, b) => {
      // "Default" (null/0 owner bucket) always pinned to the top.
      if (a.value === OPERATOR_DEFAULT_LABEL) return -1;
      if (b.value === OPERATOR_DEFAULT_LABEL) return 1;
      // Values prefixed with ❌ (deleted / inactive markers) sink to the bottom.
      const aX = a.value.startsWith("❌");
      const bX = b.value.startsWith("❌");
      if (aX !== bX) return aX ? 1 : -1;
      return a.value.localeCompare(b.value);
    });
}

export function resolveFieldLabel(groupFields: GroupFieldDef[] | undefined, field: string): string {
  return groupFields?.find((g) => g.field === field)?.label ?? field;
}

/** Label shown when an item has no operator (OperatorID null / 0). */
export const OPERATOR_DEFAULT_LABEL = "Default";

/**
 * Translate an operator id (string/number/null) to a display name.
 * Null / 0 / empty → "Default" (the system-wide / no-owner bucket). Known ids
 * resolve via the lookup map; unknown numeric ids fall through as-is so the
 * caller can still group/debug by raw id.
 */
export function formatOperatorId(raw: unknown, map: Map<string, string>): string {
  if (raw == null || raw === "" || raw === 0 || raw === "0") return OPERATOR_DEFAULT_LABEL;
  const id = String(raw);
  return map.get(id) ?? id;
}

/**
 * Build a numeric-operator-id → display-name map from the cache's flat
 * CommonData.Common list. The category field goes by several names across
 * cache versions (`ColumnName`, `TypeName`, `sName`) and we accept any that
 * resolves to "iOwnerOperatorID" (case-insensitive). Likewise the id/value
 * and display-name fields have multiple spellings.
 */
export function useOperatorNameById(
  commonData: unknown[] | undefined,
): Map<string, string> {
  return useMemo(() => {
    const m = new Map<string, string>();
    if (!commonData) return m;
    for (const entry of commonData) {
      if (!entry || typeof entry !== "object") continue;
      const c = entry as Record<string, unknown>;
      // Any property that looks like a category-name field. Collect all
      // candidate values and check each — guards against unexpected casing
      // from either the server or the client-side cache transform.
      const candidates = [c.ColumnName, c.TypeName, c.sName, c.columnName, c.typeName];
      let isOperatorRow = false;
      for (const cand of candidates) {
        if (typeof cand === "string" && cand.toLowerCase() === "iowneroperatorid") {
          isOperatorRow = true;
          break;
        }
      }
      if (!isOperatorRow) continue;
      const idRaw = c.Value ?? c.sFilterValue ?? c.value;
      if (idRaw == null || idRaw === "") continue;
      const id = String(idRaw);
      const nameRaw = c.Title ?? c.sDisplayText ?? c.title;
      const name = nameRaw != null && nameRaw !== "" ? String(nameRaw) : id;
      m.set(id, name);
    }
    // An empty result is legitimate when the endpoint has no operators.
    return m;
  }, [commonData]);
}
